import React, { useState } from 'react';
import { useNavigation } from '../navigation';

// Utility functions for localStorage management
const getUserData = () => {
  try {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

const clearUserData = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');
  localStorage.removeItem('userRole');
  localStorage.removeItem('loginTime');
  localStorage.removeItem('username');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('full_name');
  localStorage.removeItem('gstin');
  localStorage.removeItem('dl_no');
  localStorage.removeItem('pharmaDetails');
  localStorage.removeItem('pharmaEntityType');
};

const ENTITY_TYPE_API_URL =
  'https://pharma-amd-0-1-42.onrender.com/pharma/api/admin/entity-type';

const isUserLoggedIn = () => {
  const token = localStorage.getItem('authToken');
  const userData = getUserData();
  return !!(token && userData);
};

const LandingPage: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [loginData, setLoginData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'pharmacist'
  });
  const [pharmaLoginData, setPharmaLoginData] = useState({
    identifier: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isPharmaLoginOpen, setIsPharmaLoginOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check if user is already logged in on component mount - redirect to homepage
  // When not logged in (e.g. after logout), ensure pharma-related keys are removed from localStorage
  React.useEffect(() => {
    if (isUserLoggedIn()) {
      navigateTo('pharma-homepage');
    } else {
      // Clear pharma/session keys when landing on this page without auth (e.g. after logout)
      localStorage.removeItem('dl_no');
      localStorage.removeItem('pharmaDetails');
      localStorage.removeItem('full_name');
      localStorage.removeItem('gstin');
      localStorage.removeItem('pharmaEntityType');
    }
  }, []);

  // Logout function
  const handleLogout = () => {
    clearUserData();
  };


  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    // Convert to -1 to 1 range
    const normalizedX = (x - 0.5) * 2;
    const normalizedY = (y - 0.5) * 2;
    
    setMousePosition({ x: normalizedX, y: normalizedY });
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharmacist/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      if (response.ok) {
        await response.json();
        alert('Registration successful! Welcome to SPEROW-PHARMA!');
        // Reset form
        setLoginData({
          username: '',
          email: '',
          password: '',
          role: 'pharmacist'
        });
         // Close login form
         handleCloseLogin();
         // Navigate to medication addition page
         navigateTo('medication-addition');
      } else {
        const error = await response.json();
        alert(`Registration failed: ${error.message || 'Please try again'}`);
      }
    } catch {
      alert('Network error. Please check your connection and try again.');
    }
  };

  const handlePharmaLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const trimmedIdentifier = pharmaLoginData.identifier.trim();
      const trimmedPassword = pharmaLoginData.password.trim();

      if (!trimmedIdentifier) {
        setLoginError('Please provide either username or email');
        setIsLoading(false);
        return;
      }

      if (!trimmedPassword) {
        setLoginError('Password is required');
        setIsLoading(false);
        return;
      }

      const isEmailIdentifier = trimmedIdentifier.includes('@');

      const requestBody: any = {
        password: trimmedPassword,
        ...(isEmailIdentifier
          ? { email: trimmedIdentifier }
          : { username: trimmedIdentifier })
      };

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true' // Skip ngrok browser warning
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Check if access_token is provided (server uses access_token, not token)
        if (!result.access_token) {
          // Treat missing token as an auth error and force logout
          clearUserData();
          setLoginError('Login successful but no authentication token received. Please contact support.');
          setIsLoading(false);
          navigateTo('landing');
          return;
        }
        
        // DL no from login response (for localStorage) – API may send "dlno" or "dl_no"
        const dlNoFromUser = result.user?.dlno || result.user?.dl_no || result.user?.drug_license || result.user?.dl_number ||
          result.user?.drug_license_no || result.user?.license_no ||
          result.user?.profile?.dlno || result.user?.profile?.dl_no || result.user?.profile?.drug_license || null;

        // Store comprehensive user data in localStorage
        const userData = {
          token: result.access_token, // Server returns access_token, not token
          user: {
            id: result.user?.id || null,
            username: isEmailIdentifier ? null : trimmedIdentifier || null,
            email: isEmailIdentifier ? trimmedIdentifier : null,
            name: trimmedIdentifier || null,
            full_name: result.user?.full_name || result.user?.fullName || result.user?.name || null,
            role: 'pharma',
            permissions: result.user?.permissions || [],
            profile: result.user?.profile || {},
            dl_no: dlNoFromUser
          },
          loginTime: new Date().toISOString(),
          sessionId: result.sessionId || `session_${Date.now()}`,
          expiresAt: result.expiresAt || null,
          refreshToken: result.refreshToken || null,
          dl_no: dlNoFromUser
        };

        // Store all user data
        localStorage.setItem('authToken', userData.token);
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('userRole', 'pharma');
        localStorage.setItem('loginTime', userData.loginTime);
        if (userData.dl_no) {
          localStorage.setItem('dl_no', userData.dl_no);
        }

        // After successful login, trigger inventory load API
        try {
          const inventoryResponse = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/load_inventory', {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
              'Authorization': `Bearer ${userData.token}`,
            },
          });

          if (inventoryResponse.ok) {
            await inventoryResponse.json().catch(() => null);
          } else {
            await inventoryResponse.text();
          }
        } catch {
          // non-blocking
        }
        
        // After successful login, fetch doctor-clinic-info
        try {
          const doctorClinicResponse = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/doctor-clinic-info', {
            method: 'GET',
            mode: 'cors',
            headers: {
              'ngrok-skip-browser-warning': 'true',
              'Authorization': `Bearer ${userData.token}`,
            },
          });

          if (doctorClinicResponse.ok) {
            const clinicResult = await doctorClinicResponse.json().catch(() => null);
            if (clinicResult) {
              localStorage.setItem('pharmaDetails', JSON.stringify(clinicResult));

              // Extract and store GSTIN from clinic result
              const gstin = clinicResult.gst_number || clinicResult.gstin || clinicResult.gst_no || clinicResult.gst || clinicResult.gstNumber || clinicResult.gstin_number || null;
              if (gstin) {
                localStorage.setItem('gstin', gstin);
              }
              // Extract and store DL no from clinic result
              const dlNo = clinicResult.dlno || clinicResult.dl_no || clinicResult.drug_license || clinicResult.dl_number || clinicResult.drug_license_no || clinicResult.license_no || null;
              if (dlNo) {
                localStorage.setItem('dl_no', dlNo);
              }
            }
          } else {
            await doctorClinicResponse.text();
          }
        } catch {
          // non-blocking
        }

        try {
          const entityTypeResponse = await fetch(ENTITY_TYPE_API_URL, {
            method: 'GET',
            mode: 'cors',
            headers: {
              'ngrok-skip-browser-warning': 'true',
              Authorization: `Bearer ${userData.token}`,
            },
          });

          if (entityTypeResponse.ok) {
            const entityTypeJson = await entityTypeResponse.json().catch(() => null);
            if (entityTypeJson) {
              localStorage.setItem('pharmaEntityType', JSON.stringify(entityTypeJson));
            }
          } else {
            await entityTypeResponse.text();
          }
        } catch {
          // non-blocking
        }
        
        // Store individual fields for easy access
        if (userData.user.username) {
          localStorage.setItem('username', userData.user.username);
        } else {
          localStorage.removeItem('username');
        }
        if (userData.user.email) {
          localStorage.setItem('userEmail', userData.user.email);
        } else {
          localStorage.removeItem('userEmail');
        }
        if (userData.user.id) {
          localStorage.setItem('userId', userData.user.id.toString());
        }
        if (userData.user.full_name) {
          localStorage.setItem('full_name', userData.user.full_name);
        } else {
          localStorage.removeItem('full_name');
        }
        
        // Extract and store GSTIN from login response (if available)
        const gstinFromLogin = result.user?.gst_number || result.user?.gstin || result.user?.gst_no || result.user?.gst || result.user?.gstNumber ||
                               result.user?.gstin_number || result.user?.profile?.gst_number || result.user?.profile?.gstin ||
                               result.user?.profile?.gst_no || result.user?.profile?.gst || null;
        if (gstinFromLogin) {
          localStorage.setItem('gstin', gstinFromLogin);
        }
        // Extract and store DL no from login response (if available)
        const dlNoFromLogin = result.user?.dlno || result.user?.dl_no || result.user?.drug_license || result.user?.dl_number ||
                             result.user?.drug_license_no || result.user?.license_no ||
                             result.user?.profile?.dlno || result.user?.profile?.dl_no || result.user?.profile?.drug_license || null;
        if (dlNoFromLogin) {
          localStorage.setItem('dl_no', dlNoFromLogin);
        }

        // Reset form
        setPharmaLoginData({
          identifier: '',
          password: ''
        });
        
        // Close login form
        setIsPharmaLoginOpen(false);
        
        // Navigate to pharma homepage
        navigateTo('pharma-homepage');
      } else {
        let errorMessage = 'Login failed. Please check your credentials.';
        
        try {
          const error = await response.json();
          
          // Handle different error formats
          if (error.message) {
            errorMessage = error.message;
          } else if (error.error) {
            errorMessage = error.error;
          } else if (error.detail) {
            errorMessage = error.detail;
          } else if (typeof error === 'string') {
            errorMessage = error;
          } else {
            errorMessage = `Server error (${response.status}). Please try again.`;
          }
        } catch (parseError) {
          errorMessage = `Server error (${response.status}). Please try again.`;
        }
        
        // On any authentication error, clear any existing session and go back to landing
        clearUserData();
        setLoginError(errorMessage);
        navigateTo('landing');
      }
    } catch (error: any) {
      // Check for SSL/Certificate errors
      if (error.message.includes('ERR_CERT_COMMON_NAME_INVALID') || 
          error.message.includes('ERR_CERT_AUTHORITY_INVALID') ||
          error.message.includes('ERR_SSL_PROTOCOL_ERROR') ||
          error.message.includes('certificate')) {
        setLoginError('SSL Certificate Error: The server certificate is invalid. Please contact the administrator or try using HTTP instead of HTTPS.');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setLoginError('Network Error: Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setLoginError('Network error. Please check your connection and try again.');
      }
      // Any unexpected auth/network error: clear session and return to landing
      clearUserData();
      navigateTo('landing');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handlePharmaInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPharmaLoginData({
      ...pharmaLoginData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (loginError) {
      setLoginError('');
    }
  };


  const handleCloseLogin = () => {
    setIsClosing(true);
    // Use a shorter timeout for better responsiveness
    setTimeout(() => {
      setIsLoginOpen(false);
      setIsClosing(false);
    }, 600); // Match the 0.6s animation duration
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)',
        fontFamily: '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
      onMouseMove={handleMouseMove}
    >
      {/* Logo */}
      <div style={{
        position: 'absolute',
        top: '2rem',
        left: '2rem',
        zIndex: 1000,
        animation: 'fadeInUp 1s ease-out 0.2s both'
      }}>
        <div style={{
        display: 'flex',
        alignItems: 'center',
          gap: '0.75rem',
          background: 'rgba(255, 255, 255, 0.1)',
          padding: '0.75rem 1.5rem',
          borderRadius: '50px',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          {/* Logo Icon */}
        <div style={{
            width: '32px',
            height: '32px',
            background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)',
            borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          {/* Logo Text */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
        }}>
          <span style={{
              fontSize: '1.125rem',
            fontWeight: '800',
            color: 'white',
              letterSpacing: '-0.02em',
              lineHeight: '1',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}>
              SPEROW
            </span>
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.9)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              lineHeight: '1',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}>
              PHARMA
            </span>
          </div>
        </div>
        </div>

      {/* Main Content - Full Page Layout */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        fontFamily: '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        position: 'relative'
      }}>
        {/* Full Page Background Elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
                linear-gradient(45deg, transparent 25%, rgba(255, 255, 255, 0.02) 25%, rgba(255, 255, 255, 0.02) 50%, transparent 50%, transparent 75%, rgba(255, 255, 255, 0.02) 75%)
              `,
          backgroundSize: '100% 100%, 100% 100%, 60px 60px',
          animation: 'floatPattern 20s linear infinite'
        }}></div>

        {/* Floating Orbs */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: window.innerWidth < 768 ? '70px' : '120px',
          height: window.innerWidth < 768 ? '70px' : '120px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'floatOrb 6s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '15%',
          width: window.innerWidth < 768 ? '50px' : '80px',
          height: window.innerWidth < 768 ? '50px' : '80px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'floatOrb 8s ease-in-out infinite reverse'
        }}></div>

        {/* Content Container */}
        <div 
          style={{
            width: '100%',
            maxWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: window.innerWidth < 768 ? '2rem 1.5rem' : '3rem 2rem',
            position: 'relative',
            zIndex: 10
          }}
        >
            {!isPharmaLoginOpen ? (
              <>
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(255, 255, 255, 0.15)',
                  padding: window.innerWidth < 768 ? '0.4rem 1rem' : '0.5rem 1.5rem',
                  borderRadius: '50px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  marginBottom: window.innerWidth < 768 ? '1.5rem' : '2rem',
                  backdropFilter: 'blur(10px)',
                  animation: 'fadeInUp 1s ease-out 0.2s both'
                }}>
                  <span style={{
                    color: 'white',
                    fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem',
                    fontWeight: '600',
                    letterSpacing: '0.05em'
                  }}>
                    PHARMA MANAGEMENT
                  </span>
                </div>

                <h1 style={{
                fontSize: window.innerWidth < 768 ? '2.5rem' : window.innerWidth < 1024 ? '3rem' : '3.5rem',
                fontWeight: '600',
                color: 'white',
                  marginBottom: window.innerWidth < 768 ? '1rem' : '1.5rem',
                lineHeight: '1.2',
                letterSpacing: '-0.01em',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  animation: 'fadeInUp 1s ease-out 0.4s both',
                textTransform: 'uppercase'
              }}>
                For Pharma
                </h1>

                <p style={{
                fontSize: window.innerWidth < 768 ? '1rem' : window.innerWidth < 1024 ? '1.125rem' : '1.25rem',
                fontWeight: '400',
                color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: window.innerWidth < 768 ? '2rem' : '3rem',
                  lineHeight: '1.6',
                maxWidth: window.innerWidth < 768 ? '100%' : '500px',
                  animation: 'fadeInUp 1s ease-out 0.6s both'
                }}>
                Streamline your pharmacy operations with our comprehensive management platform designed for modern healthcare.
                </p>

              {/* Modern Sign In Button */}
              <div style={{ animation: 'fadeInUp 1s ease-out 0.8s both' }}>
                  <button 
                  onClick={() => setIsPharmaLoginOpen(true)}
                    style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                      padding: window.innerWidth < 768 ? '1rem 2rem' : '1.25rem 3rem',
                      borderRadius: window.innerWidth < 768 ? '12px' : '16px',
                      fontSize: window.innerWidth < 768 ? '1rem' : '1.125rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                    backdropFilter: 'blur(20px)',
                      position: 'relative',
                    overflow: 'hidden',
                    letterSpacing: '0.02em',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: window.innerWidth < 768 ? '0.5rem' : '0.75rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(27, 94, 32, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
                    Sign In
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  </button>
              </div>
              </>
            ) : (
              /* Login Form */
              <form onSubmit={handlePharmaLoginSubmit} style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: window.innerWidth < 768 ? '16px' : '20px',
                padding: window.innerWidth < 768 ? '1.5rem' : '2rem',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                animation: 'fadeInUp 1s ease-out 0.2s both'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: window.innerWidth < 768 ? '1rem' : '1.5rem'
                }}>
                  <h3 style={{
                    fontSize: window.innerWidth < 768 ? '1.25rem' : '1.5rem',
                    fontWeight: '700',
                    color: 'white',
                    margin: 0
                  }}>
                    Pharma Login
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsPharmaLoginOpen(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: 'rgba(255, 255, 255, 0.7)',
                      padding: '0.25rem'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Error Message */}
                {loginError && (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    color: '#ef4444',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    {loginError}
                  </div>
                )}
                
                <div style={{ marginBottom: window.innerWidth < 768 ? '0.75rem' : '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: '0.5rem'
                  }}>
                    Email or Username *
                  </label>
                  <input
                    type="text"
                    name="identifier"
                    value={pharmaLoginData.identifier}
                    onChange={handlePharmaInputChange}
                    placeholder="Enter email address or username"
                    required
                    style={{
                      width: '100%',
                      padding: window.innerWidth < 768 ? '0.6rem' : '0.75rem',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: window.innerWidth < 768 ? '6px' : '8px',
                      fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      fontWeight: '600',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: window.innerWidth < 768 ? '0.75rem' : '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: '0.5rem'
                  }}>
                    Password *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={pharmaLoginData.password}
                      onChange={handlePharmaInputChange}
                      placeholder="Enter password"
                      required
                      style={{
                        width: '100%',
                        padding: window.innerWidth < 768 ? '0.6rem 2.5rem 0.6rem 0.6rem' : '0.75rem 3rem 0.75rem 0.75rem',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: window.innerWidth < 768 ? '6px' : '8px',
                        fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        fontWeight: '600',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: window.innerWidth < 768 ? '0.6rem' : '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'rgba(255, 255, 255, 0.7)',
                        padding: '0.25rem'
                      }}
                    >
                      {showPassword ? (
                        <svg width={window.innerWidth < 768 ? "16" : "20"} height={window.innerWidth < 768 ? "16" : "20"} viewBox="0 0 24 24" fill="none">
                          <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width={window.innerWidth < 768 ? "16" : "20"} height={window.innerWidth < 768 ? "16" : "20"} viewBox="0 0 24 24" fill="none">
                          <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                 </div>
                
                <button
                  type="submit"
                  disabled={
                    isLoading ||
                    !pharmaLoginData.password.trim() ||
                    !pharmaLoginData.identifier.trim()
                  }
                  style={{
                    width: '100%',
                    padding: window.innerWidth < 768 ? '0.6rem 1.25rem' : '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: window.innerWidth < 768 ? '6px' : '8px',
                    background: isLoading || !pharmaLoginData.password.trim() || !pharmaLoginData.identifier.trim()
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(255, 255, 255, 0.2)',
                    color: isLoading || !pharmaLoginData.password.trim() || !pharmaLoginData.identifier.trim()
                      ? 'rgba(255, 255, 255, 0.5)'
                      : 'white',
                    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                    fontWeight: '600',
                    cursor: isLoading || !pharmaLoginData.password.trim() || !pharmaLoginData.identifier.trim()
                      ? 'not-allowed'
                      : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: window.innerWidth < 768 ? '0.4rem' : '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading && pharmaLoginData.password.trim() && pharmaLoginData.identifier.trim()) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading && pharmaLoginData.password.trim() && pharmaLoginData.identifier.trim()) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isLoading ? (
                    <>
                      <svg width={window.innerWidth < 768 ? "14" : "16"} height={window.innerWidth < 768 ? "14" : "16"} viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                        <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
                      </svg>
                      {window.innerWidth < 768 ? 'Logging...' : 'Logging in...'}
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>
            )}
        </div>

        {/* Version Info */}
        <div style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '0.75rem',
          color: 'rgba(255, 255, 255, 0.7)',
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.1)',
          padding: '0.5rem 1rem',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          zIndex: 10
        }}>
          SPEROW-PHARMA v2.0
        </div>
      </div>


      <style>{`
        input::placeholder {
          color: #d1d5db !important;
          opacity: 1;
        }
        
        input::-webkit-input-placeholder {
          color: #d1d5db !important;
          opacity: 1;
        }
        
        input::-moz-placeholder {
          color: #d1d5db !important;
          opacity: 1;
        }
        
        input:-ms-input-placeholder {
          color: #d1d5db !important;
          opacity: 1;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(100px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes geometricMove {
          0% {
            background-position: 0 0, 50px 50px;
          }
          100% {
            background-position: 100px 100px, 150px 150px;
          }
        }

        @keyframes floatOrb1 {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(-10px) translateX(-15px);
          }
          75% {
            transform: translateY(-30px) translateX(5px);
          }
        }

        @keyframes floatOrb2 {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          33% {
            transform: translateY(-25px) translateX(-10px);
          }
          66% {
            transform: translateY(-15px) translateX(20px);
          }
        }

        @keyframes floatOrb3 {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-20px) translateX(-5px);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }

        @keyframes gradientText {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 10px 20px rgba(34, 95, 225, 0.3);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 15px 30px rgba(34, 95, 225, 0.4);
          }
        }

        @keyframes orbit {
          0% {
            transform: rotate(0deg) translateX(120px) rotate(0deg);
          }
          100% {
            transform: rotate(360deg) translateX(120px) rotate(-360deg);
          }
        }

        @keyframes slideInLeft {
          0% {
            opacity: 0;
            transform: translateX(-150px) scale(0.8) rotateY(-15deg);
            filter: blur(10px);
          }
          30% {
            opacity: 0.3;
            transform: translateX(-80px) scale(0.9) rotateY(-8deg);
            filter: blur(5px);
          }
          70% {
            opacity: 0.8;
            transform: translateX(-20px) scale(0.95) rotateY(-2deg);
            filter: blur(2px);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1) rotateY(0deg);
            filter: blur(0px);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes gradientShift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes floatParticles {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          33% {
            transform: translateY(-20px) rotate(120deg);
          }
          66% {
            transform: translateY(10px) rotate(240deg);
          }
        }

        @keyframes gridMove {
          0% {
            transform: translateX(0) translateY(0);
          }
          25% {
            transform: translateX(-10px) translateY(-5px);
          }
          50% {
            transform: translateX(0) translateY(-10px);
          }
          75% {
            transform: translateX(10px) translateY(-5px);
          }
          100% {
            transform: translateX(0) translateY(0);
          }
        }

        @keyframes floatContainer {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes slideOutToLeft {
          0% {
            opacity: 1;
            transform: translateX(0) scale(1) rotateY(0deg);
            filter: blur(0px);
          }
          50% {
            opacity: 0.5;
            transform: translateX(-60px) scale(0.9) rotateY(-5deg);
            filter: blur(3px);
          }
          100% {
            opacity: 0;
            transform: translateX(-120px) scale(0.8) rotateY(-10deg);
            filter: blur(6px);
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes floatPattern {
          0% {
            background-position: 0 0, 0 0, 0 0;
          }
          100% {
            background-position: 0 0, 0 0, 60px 60px;
          }
        }

        @keyframes floatOrb {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(-10px) translateX(-15px);
          }
          75% {
            transform: translateY(-30px) translateX(5px);
          }
        }

        @keyframes springBounce {
          0% {
            transform: scale(1) translateY(0);
          }
          20% {
            transform: scale(1.05) translateY(-5px);
          }
          40% {
            transform: scale(0.98) translateY(2px);
          }
          60% {
            transform: scale(1.02) translateY(-2px);
          }
          80% {
            transform: scale(0.99) translateY(1px);
          }
          100% {
            transform: scale(1) translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
